import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { useParcelMutations } from "../hooks";
import type { StreetCandidate } from "../api";
import { ParcelRouteMiniMap } from "./ParcelRouteMiniMap";

const SOURCE_LABEL: Record<StreetCandidate["source"], string> = {
  geoportal: "Geoportal (GUGiK)",
  osm: "OpenStreetMap",
  wypis_ai: "Wypis (AI)",
};

/** "Pobierz adres": reverse-geocode the parcel + optional AI wypis reading, show it on a
 *  map, let the user pick/edit a suggestion, and save it to `street_and_number` together
 *  with the miejscowość (`locality`, source of #dzialka_miejscowosc#). Nothing is
 *  persisted until the user clicks "Zapisz". */
export function SuggestStreetModal({
  open,
  connectionId,
  parcelId,
  parcelTeryt,
  currentStreet,
  currentLocality,
  onClose,
}: {
  open: boolean;
  connectionId: string;
  parcelId: string;
  parcelTeryt: string;
  currentStreet: string;
  currentLocality: string;
  onClose: () => void;
}) {
  const pm = useParcelMutations(connectionId);
  const suggest = pm.suggestStreet;
  const [value, setValue] = useState(currentStreet);
  const [locality, setLocality] = useState(currentLocality);
  const [suggestedLocality, setSuggestedLocality] = useState("");
  const [candidates, setCandidates] = useState<StreetCandidate[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Fetch a fresh suggestion each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setErr(null);
    setValue(currentStreet);
    setLocality(currentLocality);
    setSuggestedLocality("");
    setCandidates([]);
    suggest.mutate(parcelId, {
      onSuccess: (res) => {
        setCandidates(res.candidates);
        // Prefill only when the field is empty, so we never clobber a typed value.
        setValue((v) => (v.trim() ? v : res.default));
        setSuggestedLocality(res.locality || "");
        setLocality((v) => (v.trim() ? v : res.locality || ""));
      },
      onError: (e: unknown) =>
        setErr(
          (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data
            ?.errors?.detail || "Nie udało się pobrać sugestii adresu.",
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parcelId]);

  const save = () => {
    pm.update.mutate(
      { id: parcelId, payload: { street_and_number: value.trim(), locality: locality.trim() } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title={`Pobierz adres — działka ${parcelTeryt}`} size="lg">
      <div className="space-y-4">
        <div className="h-64 border border-[var(--color-border)]">
          <ParcelRouteMiniMap connectionId={connectionId} parcelId={parcelId} />
        </div>

        {suggest.isPending && (
          <div className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <Spinner /> Szukam adresu (geoportal / OSM / wypis)…
          </div>
        )}
        {err && <p className="text-[12px] text-amber-600">{err}</p>}

        {!suggest.isPending && (
          <div>
            <p className="section-label mb-1.5">Sugestie</p>
            {candidates.length === 0 ? (
              <p className="text-[12px] text-[var(--color-text-muted)]">
                Brak adresu wg API — prawdopodobnie działka w szczerym polu (bez numeru
                porządkowego). Sugestia: „n/d”.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {candidates.map((c) => (
                  <button
                    key={c.source}
                    type="button"
                    onClick={() => setValue(c.text)}
                    className="px-2 py-1 text-[12px] border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                    title={SOURCE_LABEL[c.source]}
                  >
                    <span className="text-[var(--color-text-muted)]">{SOURCE_LABEL[c.source]}:</span>{" "}
                    <span className="font-medium">{c.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <Input
            label="Miejscowość"
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            placeholder="np. Łebcz — puste: w dokumentach nazwa obrębu / gminy"
          />
          {!suggest.isPending && suggestedLocality && suggestedLocality !== locality.trim() && (
            <button
              type="button"
              onClick={() => setLocality(suggestedLocality)}
              className="mt-1.5 px-2 py-1 text-[12px] border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
              title="Miejscowość z geokodera (Geoportal GUGiK, a gdy brak — OpenStreetMap)"
            >
              <span className="text-[var(--color-text-muted)]">Geokoder:</span>{" "}
              <span className="font-medium">{suggestedLocality}</span>
            </button>
          )}
        </div>

        <Input
          label="Ulica i numer"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="np. Polna 12, na wsi „Pilichowo 21”, w szczerym polu „n/d”"
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={save} loading={pm.update.isPending}>
            Zapisz
          </Button>
        </div>
      </div>
    </Modal>
  );
}
